/* eslint-disable no-loop-func */
import Alert from 'react-s-alert';
import { observable, action, computed, when } from 'mobx';

import { Input } from 'core';
import { whenever } from 'core/whenever';

import { rootStore } from 'apps/store';
import { clientStore } from 'apps/client/app';
import { OperationApp, type RepositoryApp } from 'apps/repository/app';

import { OperationMutation } from 'apps/repository/gql';
import type FileAppManager from 'apps/file/appManager';
import type { FileApp } from 'apps/file/app';
import type { RepoAppManager } from './appManager';

type OperationType = 'ADD' | 'REMOVE' | 'RENAME';

export class OpQueue {
  /* This queues Operation mutations, so they run one at a time.
  In the future we may look at batching mutations into a multi-Operation call */
  @observable queue: OperationMutation[] = [];
  // we could list errored ops here and allow the user to retry them or remove them
  // @observable errored = [];
  @observable running: boolean = null;
  @observable manager: RepoAppManager = null;
  @observable timer: NodeJS.Timeout = null;
  @observable runner: () => void = null;
  @observable fileManagers: FileAppManager[] = [];
  @observable localOperations = observable.map();

  stopped = true;

  constructor(manager: RepoAppManager) {
    this.manager = manager;

    this.start();
  }

  start = () => {
    this.runner = whenever(
      () => !this.running && this.queue.length > 0,
      () => this.run(),
    );
    this.stopped = false;
  };

  stop = () => {
    clientStore.uploader.abort(this.fileManagers);

    this.stopped = true;
    this.runner();
    this.clear();
  };

  @action add = (op: OperationMutation) => {
    if (!this.stopped) {
      this.queue.push(op);
    }
  };

  @action RemoveOperations = (
    repo: RepositoryApp,
    { path, file }: { path: string; file: FileApp },
  ) => {
    const opArr = Array.from(this.localOperations).reverse();
    for (let i = opArr.length - 1; i >= 0; i--) {
      const [k] = opArr[i];
      if (file?.isFolder) {
        if (
          k.startsWith(`REMOVE: ${path}/`) ||
          k.startsWith(`ADD: ${path}/`) ||
          k.startsWith(`RENAME: ${path}/`)
        ) {
          this.localOperations.delete(k);
        }
      } else if (
        k === `REMOVE: ${path}` ||
        k === `ADD: ${path}` ||
        k === `RENAME: ${path}`
      ) {
        this.localOperations.delete(k);
      }
    }
  };

  @action QueueOperation = (
    repo: RepositoryApp,
    opType: OperationType,
    {
      path,
      oldPath,
      file,
    }: {
      path: string;
      oldPath: string;
      file: FileApp;
    },
    fileManager: FileAppManager,
  ) => {
    if (fileManager) {
      this.fileManagers.push(fileManager);
    }

    const wipOp = new OperationApp();
    wipOp.opType = opType;
    wipOp.path = path;

    const opArr = Array.from(this.localOperations).reverse();
    for (let i = opArr.length - 1; i >= 0; i--) {
      const [k] = opArr[i];

      // - if we add a file, that was removed before, then we need to remove the remove op
      if (opType === 'ADD' && k === `REMOVE: ${path}`) {
        this.localOperations.delete(k);
        break;
      }

      // - if we rename a file, then remove all add, rename, remove ops for the oldPath
      // - only the rename will be kept, this is kind of wrong because we should change
      // the rename to an add/remove before we add it to localOperations, but since we
      // only use the localOperations as a counter it doesn't matter that much
      if (
        opType === 'RENAME' &&
        (k === `ADD: ${oldPath}` ||
          k === `RENAME: ${oldPath}` ||
          k === `REMOVE: ${oldPath}`)
      ) {
        this.localOperations.delete(k);
        break;
      }

      // - if we remove a file that was added or removed before, we need to remove those
      // ops
      if (
        opType === 'REMOVE' &&
        (k === `ADD: ${path}` || k === `RENAME: ${path}`)
      ) {
        this.localOperations.delete(k);
        break;
      }

      // - if we remove folder, we need to remove all files inside the folder that were
      // added, renamed or removed
      if (
        opType === 'REMOVE' &&
        (k.startsWith(`ADD: ${path}/`) ||
          k.startsWith(`RENAME: ${path}/`) ||
          k.startsWith(`REMOVE: ${path}/`))
      ) {
        this.localOperations.delete(k);
      }
    }

    // - if we remove a folder, and there are no files in that folder, then
    // we need to ignore that folder remove
    let ignoreRemove = false;
    if (opType === 'REMOVE') {
      let addedFilesInRemovedFolder = 0;
      const ignoreArr = Array.from(this.localOperations).reverse();
      for (let i = ignoreArr.length - 1; i >= 0; i--) {
        const [k] = ignoreArr[i];
        if (k.startsWith(`ADD: ${path}/`) || k.startsWith(`RENAME: ${path}/`)) {
          addedFilesInRemovedFolder += 1;
          break;
        }
      }
      if (addedFilesInRemovedFolder === 0) {
        ignoreRemove = true;
      }
    }

    // - only add to localOperations if this is not a folder create or a remove
    // for a folder with no files in it
    if (!file?.isFolder && !ignoreRemove) {
      const wipKey = `${wipOp.opType}: ${wipOp.path}`;
      this.localOperations.set(wipKey, wipOp);
    }

    when(
      () => !fileManager || fileManager.fileModelCreated,
      () => {
        const input = new Input({
          projectId: () => repo.id,
          opType: () => opType,
          path: () => path,
          oldPath: () => oldPath,
          fileId: () => file?.id || fileManager?.file?.id,
        });
        const options = {
          onSuccess: this.opSuccess,
          onUserErr: () => this.opError(fileManager?.file, opType, path),
          onSysErr: () => this.opError(fileManager?.file, opType, path),
        };
        const opMutation = new OperationMutation(input, null, options, false);
        this.add(opMutation);
      },
    );
  };

  @action opError = (file: FileApp, opType: OperationType, path: string) => {
    const alertConfig = {
      timeout: 10000,
    };
    const wipKey = `${opType}: ${path}`;
    this.localOperations.delete(wipKey);
    if (file) {
      Alert.error(
        `There was an error when trying to contribute ${path}, you may have to re-upload the contribution.`,
        alertConfig,
      );
    } else {
      Alert.error(
        'There was an error when trying to apply the change. Please try again.',
        alertConfig,
      );
    }
    this.finish();
    this.running = false;
  };

  @action run = () => {
    if (this.running) {
      return;
    }
    const op = this.queue.shift();
    if (op) {
      this.running = true;
      op.mutate();
    }
  };

  @action opSuccess = op => {
    if (op.ignored) {
      const wipKey = `${op.opType}: ${op.path}`;
      this.localOperations.delete(wipKey);
    }
    this.finish();
    this.running = false;
  };

  @action finish = () => {
    if (this.timer) {
      return;
    }

    // - we want to avoid doing the PendingOperationsQuery over and over again, since
    // it is so heavy, so when doing a single file upload we want to do it relatively
    // quick, so after 100ms is fine then
    // - but if we upload many files, then doing it every 100ms is actually still too
    // much, and doing it less speeds up the upload, so the code below tries to figure
    // out that case
    let sleepTime = 0;

    // - ok so we do this first because it is better then just going localOperations.size * 100,
    // the reason its better is because localOperations just has all operations, even the ones
    // that were just a single add or rm, so if we just use localOperations.size then
    // sleepTime would just go up and up
    // - instead we use this because if there are a ton of non complete fileManagers we know
    // that there are a lot of uploads
    for (let i = 0; i < this.fileManagers.length; i++) {
      if (!this.fileManagers[i].file.completed) {
        sleepTime += 100;
      }
      if (sleepTime >= 10000) {
        break;
      }
    }

    // - the above code works fine when there are a lot of uploads, but once all uploads
    // are finished, we still have the operation mutations pending, and especially then
    // we need to avoid doing the PendingOperations query all the time
    // - so the first check: this.queue.length > 0 && sleepTime < 1000 makes it so we only
    // rely on localOperations.size when a) queue.length > 0 meaning at least one operation
    // mutation is currently pending (queue is never larger then 1 or 2)
    // and b) sleepTime < 1000 when we already had some fileManagers non complete but less
    // then 10 then its better to not let it go back to <1000ms sleepTime cause it will just
    // slow down everything so much, better then to just check the localOperations
    if (this.queue.length > 0 && sleepTime < 1000) {
      if (this.localOperations.size > 100) {
        sleepTime = 10000;
      } else if (this.localOperations.size > 0) {
        sleepTime = this.localOperations.size * 100;
      }
    }

    // - if we end up with sleepTime 0 here then we just did a single file upload or
    // something like that and can do the query quickly
    if (sleepTime === 0) {
      sleepTime = 100;
    }

    this.timer = setTimeout(() => {
      this.manager.NewOperationsQuery();
      const { processMonitor } = rootStore.stores;
      if (processMonitor) {
        // not sure this is necessary - we should re-evaluate when we
        // need to re-run the ProcessMonitorQuery
        processMonitor.NewProcessMonitorQuery();
      }
      this.timer = null;
    }, sleepTime);
  };

  @action clear = () => {
    this.fileManagers = [];
    this.queue = [];
  };

  @computed get finished() {
    return (
      !this.timer &&
      !this.queue.length &&
      !this.running &&
      this.fileManagers.filter(fm => !fm.file.completed).length === 0
    );
  }

  @computed get length() {
    return this.fileManagers.length;
  }
}
